import { Injectable } from '@angular/core';
import { BaseWooCommerceV2ApiService } from './base-woo-commerce-v2-api.service';
import { map, Observable } from 'rxjs';
import {
  GetPaymentMethodsResponse,
  AddPaymentMethodRequest,
  AddPaymentMethodResponse,
  DeletePaymentMethodResponse,
  PaymentMethodResponse,
  PaymentMethodsSetupResponse
} from '../../../interfaces/woo-commerce/api/payment-method/payment-method.interface';
import { HappyCustomerPaymentMethod, HappySetDefaultPaymentMethodResponse } from '../../../interfaces/happy-api/happy-api.interface';

@Injectable({
  providedIn: 'root'
})
export class WooCommerceCustomerPaymentApiService extends BaseWooCommerceV2ApiService {
  private readonly happyBaseUrl: string = '/customer/payment-methods';

  getPaymentMethodsSetup(): Observable<PaymentMethodsSetupResponse> {
    return this.post<any>(`${this.happyBaseUrl}/setup`, {}).pipe(
      map((response: any) => {
        const data = response?.data || response || {};
        return {
          success: true,
          data: {
            gateway: String(data?.gateway || 'stripe'),
            gateway_id: String(data?.gateway || 'stripe'),
            publishable_key: String(data?.publishable_key || ''),
            client_secret: String(data?.client_secret || ''),
            setup_intent_id: String(data?.setup_intent_id || '')
          }
        };
      })
    );
  }

  getCustomerPaymentMethods(): Observable<GetPaymentMethodsResponse> {
    return this.get(this.happyBaseUrl).pipe(
      map((response: any) => {
        const rawMethods = this.extractDataArray(response);
        return {
          success: true,
          data: {
            payment_methods: rawMethods.map((m: any) =>
              this.mapPaymentMethod(m)
            )
          }
        };
      })
    );
  }

  /**
   * POST /customer/payment-methods
   *
   * Per Cart-Subs.md §11 Saved Payment Methods:
   *  - Stripe:       { gateway: "stripe",        payment_method: "credit_card", payment_method_data: { payment_method: "pm_xxx" } }
   *  - Authorize.Net: { gateway: "authorize_net", payment_method: "credit_card", payment_method_data: { card_number, expiration_date, card_code } }
   */
  addCustomerPaymentMethod(
    body: AddPaymentMethodRequest
  ): Observable<AddPaymentMethodResponse> {
    const gateway = body.gateway_id || 'stripe';
    let paymentMethodData: Record<string, string>;

    if (gateway === 'authorize_net' && body.cardData) {
      paymentMethodData = {
        card_number: body.cardData.card_number,
        expiration_date: body.cardData.expiration_date,
        card_code: body.cardData.card_code
      };
    } else {
      paymentMethodData = { payment_method: body.token || '' };
    }

    const payload = {
      gateway,
      payment_method: 'credit_card',
      payment_method_data: paymentMethodData,
      ...(body.is_default ? { set_default: true } : {})
    };

    return this.post(this.happyBaseUrl, payload).pipe(
      map((response: any) => {
        const method = response?.data || response || {};
        return {
          success: true,
          message: 'Payment method added',
          data: {
            payment_method: this.mapPaymentMethod(method)
          }
        };
      })
    );
  }

  setDefaultPaymentMethod(
    id: number
  ): Observable<HappySetDefaultPaymentMethodResponse> {
    return this.post<any>(`${this.happyBaseUrl}/${id}/set-default`, {}).pipe(
      map((response: any) => ({
        status: response?.status ?? true,
        message: response?.message || 'Default payment method updated'
      }))
    );
  }

  deleteCustomerPaymentMethod(
    id: number
  ): Observable<DeletePaymentMethodResponse> {
    return this.delete(`${this.happyBaseUrl}/${id}`).pipe(
      map(() => ({
        success: true,
        message: 'Payment method deleted'
      }))
    );
  }

  /**
   * Safely extract the array of methods from various API response shapes.
   * Handles: { data: [...] }, { data: { data: [...] } }, and bare arrays.
   */
  private extractDataArray(response: any): HappyCustomerPaymentMethod[] {
    const outer = response?.data ?? response;
    const inner = Array.isArray(outer) ? outer : outer?.data;
    return Array.isArray(inner) ? inner : [];
  }

  private mapPaymentMethod(method: any): PaymentMethodResponse {
    return {
      id: Number(method?.id || 0),
      gateway_id: String(method?.gateway || 'stripe'),
      type: String(method?.payment_method || 'credit_card'),
      is_default: !!method?.is_default,
      card_type: String(method?.card_brand || '').toLowerCase(),
      last4: String(method?.last_four || ''),
      exp_month: String(method?.expiry_month || ''),
      exp_year: String(method?.expiry_year || '')
    };
  }
}
